import { useCallback, useState } from 'react'
import { StepProps } from '../../types'
import { hasField, isFieldRequired, isFieldSatisfied, getFieldMaxLength } from '../../schema-utils'
import { SchemaBooleanField } from '../../schema-fields'
import { FormFloatingFooter } from '@Components/claim-flow/form-floating-footer'
import { Button } from '@Components/button/button'
import { Selector } from '@Components/selector/selector'
import PreviousChevron from '@Assets/icons/previous-chevron.svg'

import './claimant-step.scss'

const CLASS_NAME = 'claimant-step'
const SECTION = 'claimant'

export const ClaimantStep = ({ schema, geoData, formData, onSubmit, onBack }: StepProps) => {
  const existing = (formData.claimant ?? {}) as Record<string, unknown>

  const [usCitizen, setUsCitizen] = useState<boolean | undefined>(
    existing.usCitizen as boolean | undefined,
  )
  const [originalClaimant, setOriginalClaimant] = useState<boolean | undefined>(
    existing.originalClaimant as boolean | undefined,
  )
  const [countryOfResidence, setCountryOfResidence] = useState<string>(
    (existing.countryOfResidence as string) ?? '',
  )
  const [firstName, setFirstName] = useState((existing.firstName as string) ?? '')
  const [lastName, setLastName] = useState((existing.lastName as string) ?? '')
  const [middleInitial, setMiddleInitial] = useState((existing.middleInitial as string) ?? '')
  const [suffix, setSuffix] = useState((existing.suffix as string) ?? '')
  const [dateOfBirth, setDateOfBirth] = useState((existing.dateOfBirth as string) ?? '')
  const [explanation, setExplanation] = useState(
    (existing.notOriginalPurchaserExplanation as string) ?? '',
  )

  const countryOptions = geoData.countries.map((c) => ({
    label: c.name,
    value: c.name,
  }))

  const formatSSN = useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 9)
    const match = digits.match(/^(\d{0,3})(\d{0,2})(\d{0,4})$/)

    if (!match) {
      return ''
    }
    const [, area, group, serial] = match
    if (serial) {
      return `${area}-${group}-${serial}`
    }
    if (group) {
      return `${area}-${group}`
    }
    if (area) {
      return area
    }
    return ''
  }, [])

  const [ssnValue, setSsnValue] = useState(
    existing.socialSecurityNumber ? formatSSN(existing.socialSecurityNumber as string) : '',
  )

  const isValid =
    usCitizen !== undefined &&
    originalClaimant !== undefined &&
    isFieldSatisfied(schema, SECTION, 'firstName', firstName) &&
    isFieldSatisfied(schema, SECTION, 'lastName', lastName) &&
    isFieldSatisfied(schema, SECTION, 'dateOfBirth', dateOfBirth) &&
    (usCitizen === false || isFieldSatisfied(schema, SECTION, 'socialSecurityNumber', ssnValue)) &&
    (usCitizen === true || countryOfResidence !== '') &&
    (originalClaimant === true ||
      isFieldSatisfied(schema, SECTION, 'notOriginalPurchaserExplanation', explanation))

  const handleSubmit = useCallback(() => {
    if (!isValid) {
      return
    }

    const claimant: Record<string, unknown> = {}

    if (hasField(schema, SECTION, 'usCitizen')) {
      claimant.usCitizen = usCitizen
    }
    if (hasField(schema, SECTION, 'originalClaimant')) {
      claimant.originalClaimant = originalClaimant
    }
    if (hasField(schema, SECTION, 'countryOfResidence')) {
      claimant.countryOfResidence = usCitizen === true ? 'United States' : countryOfResidence
    }
    if (hasField(schema, SECTION, 'firstName')) {
      claimant.firstName = firstName
    }
    if (hasField(schema, SECTION, 'lastName')) {
      claimant.lastName = lastName
    }
    if (hasField(schema, SECTION, 'middleInitial') && middleInitial) {
      claimant.middleInitial = middleInitial
    }
    if (hasField(schema, SECTION, 'suffix') && suffix) {
      claimant.suffix = suffix
    }
    if (hasField(schema, SECTION, 'socialSecurityNumber') && usCitizen === true) {
      claimant.socialSecurityNumber = ssnValue
    }
    if (hasField(schema, SECTION, 'dateOfBirth')) {
      claimant.dateOfBirth = dateOfBirth
    }
    if (
      originalClaimant === false &&
      hasField(schema, SECTION, 'notOriginalPurchaserExplanation')
    ) {
      claimant.notOriginalPurchaserExplanation = explanation
    }

    onSubmit({ claimant })
  }, [
    isValid,
    schema,
    usCitizen,
    originalClaimant,
    countryOfResidence,
    firstName,
    lastName,
    middleInitial,
    suffix,
    ssnValue,
    dateOfBirth,
    explanation,
    onSubmit,
  ])

  return (
    <section className={CLASS_NAME}>
      <h2 className={`${CLASS_NAME}__section-title`}>Claimant Information</h2>

      <SchemaBooleanField
        schema={schema}
        section={SECTION}
        field="usCitizen"
        value={usCitizen}
        onChange={setUsCitizen}
        className={CLASS_NAME}
      />

      {hasField(schema, SECTION, 'countryOfResidence') && usCitizen === false && (
        <section className={`${CLASS_NAME}__field`}>
          <label className={`${CLASS_NAME}__label`}>Country of Residence *</label>
          <Selector<string>
            ariaLabel="Country of Residence"
            placeHolderText="Select Country..."
            selectables={countryOptions}
            value={countryOfResidence || undefined}
            onValueChange={setCountryOfResidence}
          />
        </section>
      )}

      <SchemaBooleanField
        schema={schema}
        section={SECTION}
        field="originalClaimant"
        value={originalClaimant}
        onChange={setOriginalClaimant}
        className={CLASS_NAME}
      />

      {originalClaimant === false &&
        hasField(schema, SECTION, 'notOriginalPurchaserExplanation') && (
          <section className={`${CLASS_NAME}__field`}>
            <label className={`${CLASS_NAME}__label`}>
              If not, explain{' '}
              {isFieldRequired(schema, SECTION, 'notOriginalPurchaserExplanation') && '*'}
            </label>
            <input
              type="text"
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              maxLength={getFieldMaxLength(schema, SECTION, 'notOriginalPurchaserExplanation')}
              placeholder="Received as gift"
              className={`${CLASS_NAME}__input-control`}
            />
          </section>
        )}

      <section className={`${CLASS_NAME}__row`}>
        {hasField(schema, SECTION, 'firstName') && (
          <section className={`${CLASS_NAME}__field`}>
            <label className={`${CLASS_NAME}__label`}>
              First Name {isFieldRequired(schema, SECTION, 'firstName') && '*'}
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={getFieldMaxLength(schema, SECTION, 'firstName')}
              className={`${CLASS_NAME}__input-control`}
            />
          </section>
        )}
        {hasField(schema, SECTION, 'lastName') && (
          <section className={`${CLASS_NAME}__field`}>
            <label className={`${CLASS_NAME}__label`}>
              Last Name {isFieldRequired(schema, SECTION, 'lastName') && '*'}
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              maxLength={getFieldMaxLength(schema, SECTION, 'lastName')}
              className={`${CLASS_NAME}__input-control`}
            />
          </section>
        )}
      </section>

      <section className={`${CLASS_NAME}__row`}>
        {hasField(schema, SECTION, 'middleInitial') && (
          <section className={`${CLASS_NAME}__field`}>
            <label className={`${CLASS_NAME}__label`}>Middle Initial</label>
            <input
              type="text"
              value={middleInitial}
              onChange={(e) => setMiddleInitial(e.target.value)}
              maxLength={getFieldMaxLength(schema, SECTION, 'middleInitial') ?? 1}
              className={`${CLASS_NAME}__input-control`}
            />
          </section>
        )}
        {hasField(schema, SECTION, 'suffix') && (
          <section className={`${CLASS_NAME}__field`}>
            <label className={`${CLASS_NAME}__label`}>Suffix</label>
            <input
              type="text"
              value={suffix}
              onChange={(e) => setSuffix(e.target.value)}
              maxLength={getFieldMaxLength(schema, SECTION, 'suffix')}
              className={`${CLASS_NAME}__input-control`}
            />
          </section>
        )}
      </section>

      {hasField(schema, SECTION, 'socialSecurityNumber') && usCitizen === true && (
        <section className={`${CLASS_NAME}__field`}>
          <label className={`${CLASS_NAME}__label`}>
            Social Security Number {isFieldRequired(schema, SECTION, 'socialSecurityNumber') && '*'}
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={ssnValue}
            onChange={(e) => setSsnValue(formatSSN(e.target.value))}
            placeholder="123-45-6789"
            maxLength={11}
            className={`${CLASS_NAME}__input-control`}
          />
          <p className={`${CLASS_NAME}__field-hint`}>
            Protected by Federal Privacy and State Confidentiality Laws.
          </p>
        </section>
      )}

      {hasField(schema, SECTION, 'dateOfBirth') && (
        <section className={`${CLASS_NAME}__field`}>
          <label className={`${CLASS_NAME}__label`}>
            Date of Birth {isFieldRequired(schema, SECTION, 'dateOfBirth') && '*'}
          </label>
          <input
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            className={`${CLASS_NAME}__input-control`}
          />
        </section>
      )}

      <FormFloatingFooter>
        <section className="floating-footer__buttons">
          <Button
            className="floating-footer__next-button"
            disabled={!isValid}
            onClick={handleSubmit}>
            Next
          </Button>
          <Button className="floating-footer__previous-button" onClick={onBack}>
            {' '}
            <img src={PreviousChevron} alt="previous-icon" />
          </Button>
        </section>
      </FormFloatingFooter>
    </section>
  )
}
